/**
 * Shared thinking/reasoning content selector.
 *
 * Single source of truth used by both the activity panel and the
 * transcript bubble. Any surface that needs to render the model's
 * reasoning (Assistant turn bubble, Activity dock, future Inspector
 * sub-tab, webchat copy-to-markdown, etc.) should import this module
 * instead of calling `extractThinking` directly or re-implementing
 * the per-subagent / live-stream merge.
 *
 * The selector accepts the same session-scoped inputs the chat
 * already has on hand (`messages`, `reasoningStream`, `stream`,
 * `subagents`) and a query that scopes the result to a particular
 * message or to the whole session.
 *
 * Example — full session (Activity panel, transcript copy-as-markdown):
 * ```ts
 * const all = selectThinkingContent(sessionParams, { scope: "session" });
 * // all.lines: ActivityThinkingLine[]   — reverse-chronological feed
 * // all.summary: string                — "I'm now working" one-liner
 * ```
 *
 * Example — single message (transcript bubble for a given turn):
 * ```ts
 * const bubble = selectThinkingContent(sessionParams, {
 *   scope: "message",
 *   messageIndex: 4,
 * });
 * // bubble.text:    string | null
 * // bubble.source:  "history" | "live" | "subagent"
 * ```
 *
 * JAISIU-3752 — the "I'm now working" one-liner follows the same
 * priority as the existing `resolveLiveThinkingSummary`:
 *   1. `reasoningStream`  (active reasoning delta)
 *   2. `stream`           (the main response stream)
 *   3. most recent historical assistant thinking
 */

import { extractThinking } from "./message-extract.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

/** Per-line thinking record — what gets rendered in the Activity dock. */
export type ThinkingLine = {
  id: string;
  at: number;
  source: string;
  text: string;
};

/** Provenance of a thinking record — useful for the transcript bubble
 *  which wants to label the bubble as "live" vs "history" vs
 *  "subagent". */
export type ThinkingSource = "history" | "live" | "subagent";

/** The canonical return shape for a single message. `text === null`
 *  means "no thinking content for this message". `source` is the
 *  provenance label the caller should render. */
export type ThinkingForMessage = {
  text: string | null;
  source: ThinkingSource;
  /** Stable id matching the activity stream's `ActivityThinkingLine.id`
   *  schema — useful when the caller wants to anchor or diff. */
  id: string | null;
};

/** Full session result — used by the Activity panel and the
 *  "I'm now working" pill. */
export type ThinkingForSession = {
  /** Reverse-chronological thinking lines (historical + live + subagent). */
  lines: ThinkingLine[];
  /** One-line summary suitable for the live bubble / pill. */
  summary: string;
  /** Provenance of `summary` — `null` when there is no thinking. */
  summarySource: ThinkingSource | null;
  /** True when at least one line came from the live reasoning stream
   *  (i.e. the model is still streaming). */
  hasLive: boolean;
};

/** Scope the selector to a single message. Either `messageId` or
 *  `messageIndex` must be provided when `scope === "message"`. */
export type ThinkingSelectorQuery =
  | { scope: "session"; sessionId?: string }
  | {
      scope: "message";
      sessionId?: string;
      /** Stable id from the message object (e.g. `message.id`,
       *  `message.messageId`, or any unique key the caller carries). */
      messageId?: string | null;
      /** Fallback: ordinal index into `params.messages`. */
      messageIndex?: number;
    };

/** Session-scoped inputs. Mirrors the shape `collectActivityState`
 *  already accepts so callers can hand the same `props` object to
 *  both without re-shaping. */
export type ThinkingSelectorParams = {
  messages: unknown[];
  chatToolMessages?: unknown[];
  reasoningStream?: string | null;
  stream?: string | null;
  subagents?: ChatSubagentCard[];
  /** When false, historical reasoning is omitted from session results.
   *  Live stream lines are still included — they're a separate
 *  channel. */
  showThinking?: boolean;
  /** Hard cap on subagent event expansion. Defaults to 200 to match
 *  `MAX_EVENTS_PER_CARD_FOR_ACTIVITY` — the activity panel and the
 *  bubble do not need the full ring buffer. */
  maxEventsPerSubagent?: number;
};

const DEFAULT_MAX_EVENTS_PER_SUBAGENT = 200;

function readMessageId(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const m = message as Record<string, unknown>;
  for (const key of ["id", "messageId", "message_id", "clientId", "client_id"] as const) {
    const v = m[key];
    if (typeof v === "string" && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

function firstNonEmptyLine(text: string, max = 140): string {
  const line = text
    .split("\n")
    .map((part) => part.trim())
    .find((part) => part.length > 0);
  if (!line) {
    return "";
  }
  if (line.length <= max) {
    return line;
  }
  return `${line.slice(0, max - 1)}…`;
}

/** Resolve a stable id for a per-message thinking record. Falls back
 *  to `msg-thinking:{index}` when the message has no id — this matches
 *  the convention `collectActivityState` already uses. */
function makeMessageThinkingId(message: unknown, index: number): string {
  const id = readMessageId(message);
  return id ? `msg-thinking:${id}` : `msg-thinking:${index}`;
}

/** Pull the thinking text for a single message — equivalent to
 *  `extractThinking(message)` but with provenance. */
export function selectMessageThinking(
  message: unknown,
  index: number,
): ThinkingForMessage {
  const text = extractThinking(message);
  if (!text || !text.trim()) {
    return { text: null, source: "history", id: null };
  }
  return {
    text: text.trim(),
    source: "history",
    id: makeMessageThinkingId(message, index),
  };
}

/** Pull the live `reasoningStream` (if non-empty) as a single line. */
function selectLiveReasoning(
  params: ThinkingSelectorParams,
  now: number,
): ThinkingLine | null {
  const text = params.reasoningStream?.trim();
  if (!text) {
    return null;
  }
  return {
    id: "stream:reasoning",
    at: now,
    source: "Session · live",
    text,
  };
}

/** Pull the live `stream` (if non-empty) as a single line. */
function selectLiveStream(params: ThinkingSelectorParams, now: number): ThinkingLine | null {
  const text = params.stream?.trim();
  if (!text) {
    return null;
  }
  return {
    id: "stream:main",
    at: now,
    source: "Session · live",
    text,
  };
}

/** Pull thinking from a subagent card's stream text and `thinking`
 *  events. Returns 0..N lines. */
function selectSubagentThinking(
  card: ChatSubagentCard,
  cap: number,
): ThinkingLine[] {
  const out: ThinkingLine[] = [];
  const source = card.label || card.agentId || "Subagent";
  if (card.streamText?.trim()) {
    out.push({
      id: `sub-stream:${card.id}`,
      at: card.updatedAt ?? card.createdAt,
      source,
      text: card.streamText.trim().slice(0, 8000),
    });
  }
  for (const event of (card.events ?? []).slice(0, cap)) {
    const label = event.label ?? "";
    if (event.phase === "thinking" || label.toLowerCase().includes("thinking")) {
      out.push({
        id: `sub-ev:${card.id}:${event.seq}`,
        at: event.ts,
        source,
        text: label,
      });
    }
  }
  return out;
}

/**
 * Resolve the canonical thinking content for a session. Convenience
 * wrapper around `selectThinkingContent({ scope: "session" })` that
 * narrows the return type to `ThinkingForSession` (no `scope: "message"`
 * union) so callers that only ever want the session view don't have to
 * disambiguate. Both this and `selectThinkingContent` are equivalent
 * for the session case.
 */
export function selectSessionThinking(
  params: ThinkingSelectorParams,
  options?: { sessionId?: string },
): ThinkingForSession {
  return selectThinkingContent(
    params,
    options?.sessionId ? { scope: "session", sessionId: options.sessionId } : { scope: "session" },
  ) as ThinkingForSession;
}

/**
 * Resolve the canonical thinking content for a session or a specific
 * message. The same function powers the activity panel and the
 * transcript bubble — both go through this entry point so the
 * operator sees the same content regardless of which surface they
 * look at.
 *
 * `params` carries the session-scoped state; `query` scopes the
 * result. Overloads narrow the return type to either
 * `ThinkingForSession` (when `query.scope === "session"`) or
 * `ThinkingForMessage` (when `query.scope === "message"`) so callers
 * don't have to disambiguate a union.
 */
export function selectThinkingContent(
  params: ThinkingSelectorParams,
  query: { scope: "session"; sessionId?: string },
): ThinkingForSession;
export function selectThinkingContent(
  params: ThinkingSelectorParams,
  query: {
    scope: "message";
    sessionId?: string;
    messageId?: string | null;
    messageIndex?: number;
  },
): ThinkingForMessage;
export function selectThinkingContent(
  params: ThinkingSelectorParams,
  query: ThinkingSelectorQuery,
): ThinkingForSession | ThinkingForMessage {
  const showThinking = params.showThinking !== false;
  const cap = params.maxEventsPerSubagent ?? DEFAULT_MAX_EVENTS_PER_SUBAGENT;
  const now = Date.now();
  const messages = params.messages ?? [];
  const subagents = params.subagents ?? [];

  if (query.scope === "message") {
    let index: number;
    let message: unknown;
    if (query.messageIndex != null && query.messageIndex >= 0 && query.messageIndex < messages.length) {
      index = query.messageIndex;
      message = messages[index];
    } else if (query.messageId) {
      const found = messages.findIndex((m) => readMessageId(m) === query.messageId);
      if (found < 0) {
        return { text: null, source: "history", id: null };
      }
      index = found;
      message = messages[index];
    } else {
      return { text: null, source: "history", id: null };
    }

    // Priority for a single message: if the requested message is the
    // *latest* assistant message, the live stream (when populated)
    // is the most accurate "what it's thinking now" — fall back to
    // the stored `extractThinking` text for older messages.
    const isLatest = index === messages.length - 1;
    if (isLatest) {
      const live = selectLiveReasoning(params, now);
      if (live) {
        return { text: live.text, source: "live", id: live.id };
      }
      const liveMain = selectLiveStream(params, now);
      if (liveMain) {
        return { text: liveMain.text, source: "live", id: liveMain.id };
      }
    }

    return selectMessageThinking(message, index);
  }

  // scope === "session"
  const lines: ThinkingLine[] = [];

  if (showThinking) {
    for (let i = 0; i < messages.length; i += 1) {
      const t = selectMessageThinking(messages[i], i);
      if (t.text) {
        lines.push({
          id: t.id ?? `msg-thinking:${i}`,
          at: i,
          source: "Session",
          text: t.text.slice(0, 8000),
        });
      }
    }
  }

  for (const card of subagents) {
    for (const line of selectSubagentThinking(card, cap)) {
      lines.push(line);
    }
  }

  const liveReasoning = selectLiveReasoning(params, now);
  if (liveReasoning) {
    lines.push(liveReasoning);
  }
  const liveMain = selectLiveStream(params, now);
  if (liveMain) {
    lines.push(liveMain);
  }

  // Chronological order — the activity panel reverses this for display.
  lines.sort((a, b) => a.at - b.at);

  // Summary priority: live reasoning > live main stream > most recent
  // historical line. Matches `resolveLiveThinkingSummary` so the
  // operator sees the same one-liner everywhere.
  let summary = "";
  let summarySource: ThinkingSource | null = null;
  if (liveReasoning) {
    summary = firstNonEmptyLine(liveReasoning.text);
    summarySource = "live";
  } else if (liveMain) {
    summary = firstNonEmptyLine(liveMain.text);
    summarySource = "live";
  } else if (lines.length > 0) {
    const last = lines[lines.length - 1]!;
    summary = firstNonEmptyLine(last.text);
    summarySource = last.id.startsWith("sub-") ? "subagent" : "history";
  }

  return {
    lines,
    summary,
    summarySource,
    hasLive: Boolean(liveReasoning || liveMain),
  };
}
